import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { User, LoginRequest } from '@/types/auth'
import { authAPI } from '@/api/auth'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (credentials: LoginRequest) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check if user is already logged in
    console.log('AuthContext - Checking existing authentication')
    const storedUser = authAPI.getCurrentUser()
    const isAuth = authAPI.isAuthenticated()
    console.log('AuthContext - Stored user:', storedUser, 'Is authenticated:', isAuth)

    if (storedUser && isAuth) {
      setUser(storedUser)
    }
    setIsLoading(false)
  }, [])

  const login = async (credentials: LoginRequest) => {
    console.log('AuthContext - Login attempt:', credentials.username)
    const response = await authAPI.login(credentials)
    console.log('AuthContext - Login successful, user:', response.user)
    setUser(response.user)
  }

  const logout = async () => {
    console.log('AuthContext - Logout')
    await authAPI.logout()
    setUser(null)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}